/**
 * Enhanced Author/Expert structured data (JSON-LD) for E-E-A-T signals
 * Provides author credentials, expertise, and affiliation for GEO optimization
 * Helps generative engines weight content from identifiable experts higher
 * Follows Schema.org Person specification
 */

import React from 'react';
import { JSONLDScript } from './jsonld-script';

interface AuthorStructuredDataProps {
  /** Author name */
  name: string;
  /** Author job title */
  jobTitle?: string;
  /** Organization affiliation */
  affiliation?: string;
  /** Author URL (profile page) */
  url?: string;
  /** External profile URLs (LinkedIn, Twitter, etc.) */
  sameAs?: string[];
  /** Areas of expertise */
  knowsAbout?: string[];
}

/**
 * Enhanced author structured data for E-E-A-T and GEO
 *
 * Provides detailed author credentials that generative engines use to
 * assess content authority. Content from identified experts with credentials
 * gets higher citation rates from AI-powered search engines.
 *
 * @example
 * ```tsx
 * <AuthorStructuredData
 *   name="Email Ferret Team"
 *   jobTitle="Email Security Experts"
 *   affiliation="Email Ferret"
 *   knowsAbout={['email security', 'AI spam detection', 'Gmail filtering']}
 * />
 * ```
 */
export function AuthorStructuredData({
  name = 'Email Ferret Team',
  jobTitle = 'Email Security Experts',
  affiliation = 'Email Ferret',
  url,
  sameAs = ['https://twitter.com/email_ferret', 'https://www.linkedin.com/company/110188068'],
  knowsAbout = [
    'email security',
    'AI spam detection',
    'cold outreach detection',
    'Gmail filtering',
    'email privacy',
    'heuristic analysis',
  ],
}: AuthorStructuredDataProps) {
  const baseUrl =
    process.env.NEXT_PUBLIC_APP_URL || process.env.APP_URL || 'https://emailferret.io';

  const authorSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name,
    ...(jobTitle && { jobTitle }),
    ...(affiliation && {
      affiliation: {
        '@type': 'Organization',
        name: affiliation,
        url: baseUrl,
      },
    }),
    url: url || `${baseUrl}/about`,
    ...(sameAs.length > 0 && { sameAs }),
    ...(knowsAbout.length > 0 && { knowsAbout }),
  };

  return <JSONLDScript data={authorSchema} id="author-structured-data" />;
}
